import { ColumnDef } from "@tanstack/react-table";
import { Link } from "react-router-dom";
import AdvanceTable from "components/base/AdvanceTable";
import AdvanceTableFooter from "components/base/AdvanceTableFooter";
import Badge from "components/base/Badge";
import { FornecedorType } from "pages/retaguarda/fornecedores";
import { format } from "date-fns";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser, faBuilding } from "@fortawesome/free-solid-svg-icons";

export const fornecedorTableColumns: ColumnDef<FornecedorType>[] = [
  {
    accessorKey: "codigo",
    header: "Código",
    cell: ({ getValue, row: { original } }) => {
      const codigo = getValue() as number;
      const { isDeleted } = original;
      return <span className={isDeleted ? 'text-muted' : ''}>{codigo}</span>;
    },
    meta: {
      headerProps: { style: { width: 80 }, className: "ps-4" },
      cellProps: {
        className: "fs-9 fw-semibold ps-4 text-body-tertiary text-opacity-85",
      },
    },
  },
  {
    accessorKey: "razaoSocial",
    header: "Nome/Razão Social",
    meta: {
      headerProps: { style: { width: 300 }, className: "ps-4" },
      cellProps: {
        className: "fs-9 fw-semibold ps-4 text-body-tertiary text-opacity-85",
      },
    },
    cell: ({ row }) => {
      const data = row.original;
      const { isDeleted, deletedAt, tipoPessoa } = data;
      
      return (
        <div className="d-flex flex-column position-relative">
          <Link
            to={`/cadastros/fornecedores/${data.id}`}
            className={`fw-semibold mb-1 ${isDeleted ? 'text-decoration-line-through text-muted' : ''}`}
          >
            {tipoPessoa === 1 
              ? (data.nome || '—') // Pessoa Física: só o nome
              : (data.razaoSocial || '—') // Pessoa Jurídica: razão social
            }
          </Link>
          {/* Para Pessoa Jurídica, mostrar nome fantasia embaixo se existir e for diferente */}
          {tipoPessoa === 2 && data.nomeFantasia && data.nomeFantasia !== data.razaoSocial && (
            <div className="small text-body-secondary mt-1" style={{ fontStyle: 'italic' }}>
              {data.nomeFantasia}
            </div>
          )}
          {isDeleted && deletedAt && (
            <div className="small text-danger-emphasis mt-1">
              Excluído em {format(new Date(deletedAt), 'dd/MM/yyyy HH:mm')}
            </div>
          )}
        </div>
      );
    },
  },
  {
    accessorKey: "cnpj",
    header: ({ table }) => {
      // Verificar se há dados na tabela para determinar o header dinamicamente
      const rows = table.getRowModel().rows;
      const hasData = rows.length > 0;
      
      if (hasData) {
        // Verificar os tipos de pessoa presentes nos dados
        const tiposPessoa = rows.map(row => row.original.tipoPessoa);
        const temPessoaFisica = tiposPessoa.includes(1);
        const temPessoaJuridica = tiposPessoa.includes(2);
        
        if (temPessoaFisica && temPessoaJuridica) {
          return "CPF/CNPJ";
        } else if (temPessoaFisica) {
          return "CPF";
        } else {
          return "CNPJ";
        }
      }
      
      return "CPF/CNPJ";
    },
    cell: ({ row: { original } }) => {
      const { isDeleted, tipoPessoa, cnpj, cpf } = original;
      
      // Determinar qual documento usar baseado no tipo de pessoa
      const documento = tipoPessoa === 1 ? cpf : cnpj;
      
      if (!documento) return <span className="text-muted">—</span>;
      
      const formatDocumento = (doc: string, tipo: number) => {
        const numbers = doc.replace(/\D/g, '');
        
        if (tipo === 1) {
          // CPF: 000.000.000-00
          return numbers.replace(
            /^(\d{3})(\d{3})(\d{3})(\d{2})$/,
            '$1.$2.$3-$4'
          );
        } else {
          // CNPJ: 00.000.000/0001-00
          return numbers.replace(
            /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
            '$1.$2.$3/$4-$5'
          );
        }
      };
      
      return <span className={isDeleted ? 'text-muted' : ''}>{formatDocumento(documento, tipoPessoa)}</span>;
    },
    meta: {
      headerProps: { style: { width: 150 } },
      cellProps: { className: "fs-9" },
    },
  },
  {
    accessorKey: "telefone",
    header: "Telefone",
    cell: ({ getValue, row: { original } }) => {
      const telefone = getValue() as string | undefined;
      const { isDeleted } = original;
      
      if (!telefone) return <span className="text-muted">—</span>;
      
      // Format phone number based on length
      const formatTelefone = (tel: string) => {
        // Remove non-numeric characters
        const numbers = tel.replace(/\D/g, '');
        
        // Format according to Brazilian phone number patterns
        if (numbers.length === 11) {
          // Cellphone with area code: (11) 98765-4321
          return numbers.replace(/^(\d{2})(\d{5})(\d{4})$/, '($1) $2-$3');
        } else if (numbers.length === 10) {
          // Landline with area code: (11) 3456-7890
          return numbers.replace(/^(\d{2})(\d{4})(\d{4})$/, '($1) $2-$3');
        } else if (numbers.length === 9) {
          // Cellphone without area code: 98765-4321
          return numbers.replace(/^(\d{5})(\d{4})$/, '$1-$2');
        } else if (numbers.length === 8) {
          // Landline without area code: 3456-7890
          return numbers.replace(/^(\d{4})(\d{4})$/, '$1-$2');
        }
        // For any other pattern, return as is
        return tel;
      };
      
      return <span className={isDeleted ? 'text-muted' : ''}>{formatTelefone(telefone)}</span>;
    },
    meta: {
      headerProps: { style: { width: 120 } },
      cellProps: { className: "fs-9" },
    },
  },
  {
    accessorKey: "tipoPessoa",
    header: "Tipo",
    cell: ({ getValue, row: { original } }) => {
      const tipoPessoa = getValue() as number;
      const { isDeleted } = original;
      
      if (tipoPessoa === 1) {
        // Pessoa Física
        return (
          <Badge 
            className="badge-subtle rounded-pill d-flex align-items-center justify-content-center gap-1"
            style={{ 
              backgroundColor: isDeleted 
                ? '#6c757d' 
                : '#452996',
              color: 'white',
              border: 'none',
              padding: '6px 12px',
              fontSize: '0.75rem',
              fontWeight: '500',
              minWidth: '85px',
              opacity: isDeleted ? 0.6 : 0.9,
              boxShadow: isDeleted 
                ? 'none' 
                : '0 1px 4px rgba(92, 65, 170, 0.2)',
              transition: 'all 0.2s ease-in-out'
            }}
          >
            <FontAwesomeIcon 
              icon={faUser} 
              style={{ 
                fontSize: '0.7rem'
              }}
            />
            Física
          </Badge>
        );
      } else {
        // Pessoa Jurídica
        return (
          <Badge 
            className="badge-subtle rounded-pill d-flex align-items-center justify-content-center gap-1"
            style={{ 
              background: isDeleted 
                ? '#6c757d' 
                : '#0aa3dd',
              color: 'white',
              border: 'none',
              padding: '6px 12px',
              fontSize: '0.75rem',
              fontWeight: '500',
              minWidth: '85px',
              opacity: isDeleted ? 0.6 : 0.9,
              boxShadow: isDeleted 
                ? 'none' 
                : '0 1px 4px rgba(10, 163, 221, 0.2)',
              transition: 'all 0.2s ease-in-out'
            }}
          >
            <FontAwesomeIcon 
              icon={faBuilding} 
              style={{ 
                fontSize: '0.7rem'
              }}
            />
            Jurídica
          </Badge>
        );
      }
    },
    meta: {
      headerProps: { style: { width: 120 } },
      cellProps: { className: "text-center" },
    },
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row: { original } }) => {
      const { isDeleted } = original;

      if (isDeleted) {
        return <Badge bg="danger" className="badge-subtle rounded-pill">Excluído</Badge>;
      }

      return <Badge bg="success" className="badge-subtle rounded-pill">Ativo</Badge>;
    },
    meta: {
      headerProps: { style: { width: 100 } },
      cellProps: { className: "text-center" },
    },
  },
];

const FornecedoresTable = () => {
  return (
    <div>
      <AdvanceTable tableProps={{ className: "phoenix-table fs-9" }} />
      <AdvanceTableFooter pagination showViewAllBtn={false} />
    </div>
  );
};

export default FornecedoresTable;
